// MACHINERY V2 (Stroje) — the two PIECES pattern B is made of (M2.16c split).
//
// >>> WHY THIS FILE EXISTS: THE CATALOG LAYS PATTERN B OUT DIFFERENTLY, AND
// THAT MUST NOT MEAN A SECOND SEARCH FIELD. <<<
// The fleet puts the search field and the Filtre button on ONE row
// (mobile/screenshots/01-filter-b-chosen.png, and FilterBarB.tsx is that row).
// The katalog splits them: a full-width search row, then a row holding Filtre
// TOGETHER WITH the category chips (mobile/screenshots/16-katalog-list.png).
// Same two controls, different arrangement.
//
// M2.16a's own review had to DELETE two "slightly different" copies of its own
// atoms, so the second consumer is the moment to extract rather than the moment
// to paste: both pieces move here verbatim and FilterBarB composes them. The
// fleet's rendering is unchanged — same elements, same styles, same order — it
// just no longer owns them alone.
//
// >>> THE PILL VARIANT IS A PAINT, NOT A SECOND BUTTON. <<<
// In the katalog's row 2 the Filtre control sits shoulder to shoulder with
// TouchChip category chips, whose painted pill is 34px inside a 44px target
// (TouchChip.tsx). A 44px-painted Filtre button beside 34px-painted chips is a
// ragged row, so `pill` paints this control the same way — 44px transparent
// target, 34px pill inside it — while the aria-label, the glyph, the badge and
// the badge's hidden-at-zero rule stay ONE implementation. The fleet passes
// nothing and keeps its 44px/radius-10 button exactly as it shipped.
//
// >>> THE BADGE'S NEGATIVE OFFSETS SURVIVE A SCROLLER, BUT ONLY WITH PADDING.
// <<< The badge is `top: -5; right: -5` on its box. Inside ChipScroller,
// `overflow-x: auto` forces the block axis to `auto` as well (CSS overflow: a
// non-visible value on one axis computes `visible` on the other to `auto`), so
// the badge is clipped unless the row reserves room. ChipScroller's
// `paddingBlock` is that room and the katalog row passes 8 — more than the 5px
// the badge needs. Drop that padding and the badge loses its top edge.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { MbDismissGlyph, MbFilterGlyph, MbSearchGlyph } from "./mobileGlyphs";

export interface SearchFieldBProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  /**
   * The trailing clear button. OPTIONAL, and absent means no button at all —
   * so the fleet's row is unchanged and the sheet's brand box, which filters a
   * list rather than the results, does not grow a control it has no use for.
   *
   * >>> IT IS NOT DECORATION ON THE SURFACE THAT PASSES IT (M2.16c review, item
   * 4). <<< The katalog search is deliberately NOT an active-filter chip
   * (catalogActiveFilters.ts:13-21), so `ActiveFilterChips` renders nothing for
   * it: below 1280 a search that returned zero results had no exit but
   * select-all-delete. The desktop box has had `onClear` since M2.8. Pass the
   * hook's `clearSearch`, never a bare `onChange("")` — only that one path
   * also cancels the pending 300 ms debounce (useCatalogBrowse.ts).
   */
  onClear?: () => void;
  /** Defaults to the fleet's "Hladat stroj, S/N…"; the katalog searches models. */
  placeholder?: string;
  /**
   * The field's own ground. Defaults to `var(--card)`, which is right on a PAGE
   * (both pattern-B rows sit on `var(--bg)`), and wrong INSIDE a sheet — SheetV2
   * paints its body `var(--card)` too, so the default would put a card-coloured
   * box on a card-coloured ground and leave only its border doing any work. The
   * katalog sheet's brand box therefore passes `var(--bg-soft)`. A prop rather
   * than a second search field: one control, two grounds.
   */
  background?: string;
}

/** Pattern B's search field: h44, radius 10, leading glyph. */
export const SearchFieldB: React.FC<SearchFieldBProps> = ({
  value,
  onChange,
  onSubmit,
  onClear,
  placeholder,
  background = "var(--card)",
}) => {
  const { translate } = useTranslation("myMachinery");
  const text = placeholder || translate("myMachinery:v2.mobile.search");

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        height: 44,
        padding: "0 12px",
        borderRadius: 10,
        background,
        border: "1px solid var(--border)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "var(--faint)",
      }}
    >
      <MbSearchGlyph />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSubmit();
        }}
        aria-label={text}
        placeholder={text}
        style={{
          flex: 1,
          minWidth: 0,
          border: 0,
          outline: "none",
          background: "transparent",
          fontFamily: "inherit",
          fontSize: 13,
          color: "var(--ink)",
        }}
      />
      {/* Only while there is something to clear — a standing ✕ on an empty box
          is a control that does nothing, the same rule that hides the Filtre
          badge at zero. The field's own 44px height is the target's height;
          `minWidth: 44` gives it the other axis, and the negative right margin
          keeps the 12px optical inset the field was drawn with while the target
          itself reaches the edge. */}
      {onClear && value.length > 0 && (
        <button
          type="button"
          onClick={onClear}
          aria-label={translate("myMachinery:v2.mobile.clear")}
          style={{
            alignSelf: "stretch",
            minWidth: 44,
            marginRight: -12,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            border: 0,
            background: "transparent",
            padding: 0,
            cursor: "pointer",
            color: "var(--faint)",
            fontFamily: "inherit",
          }}
        >
          <MbDismissGlyph />
        </button>
      )}
    </div>
  );
};

export interface FiltreButtonProps {
  /** Number of active filter GROUPS. Zero HIDES the badge (README §THE
   *  DECISION anatomy point 1): a standing "0" reads as a filter that is set. */
  activeCount: number;
  onClick: () => void;
  /** Paints as a 34px chip inside the 44px target — see the header. */
  pill?: boolean;
}

/** The Filtre(n) control. `className="btn"` is kept exactly as the design keeps
 *  it: hover and focus-visible live in machinery-v2-tokens.scss and cannot be
 *  expressed inline. */
export const FiltreButton: React.FC<FiltreButtonProps> = ({
  activeCount,
  onClick,
  pill,
}) => {
  const { translate } = useTranslation("myMachinery");

  const badge = activeCount > 0 && (
    // aria-hidden: the button's own aria-label already carries the count, and a
    // bare numeral read after the label is noise.
    <span
      aria-hidden
      style={{
        position: "absolute",
        top: -5,
        right: -5,
        width: 17,
        height: 17,
        borderRadius: 999,
        background: "var(--mod-accent)",
        color: "var(--mod-accent-fg)",
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        fontWeight: 700,
        display: "grid",
        placeItems: "center",
      }}
    >
      {activeCount}
    </span>
  );

  const label =
    activeCount > 0
      ? translate("myMachinery:v2.mobile.filtersActive", { count: activeCount })
      : translate("myMachinery:v2.fleet.filters");

  if (pill) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-haspopup="dialog"
        aria-label={label}
        style={{
          // 44x44 minimum, transparent — identical construction to TouchChip,
          // so the two sit on one baseline in the katalog's row 2.
          height: 44,
          minWidth: 44,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          border: 0,
          background: "transparent",
          padding: 0,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        <span
          style={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 34,
            padding: "0 12px",
            borderRadius: 999,
            background: "var(--mod-accent-soft)",
            border: "1px solid var(--mod-accent-line)",
            color: "var(--mod-accent)",
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          <MbFilterGlyph />
          {translate("myMachinery:v2.fleet.filters")}
          {badge}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className="btn"
      onClick={onClick}
      aria-haspopup="dialog"
      aria-label={label}
      style={{
        height: 44,
        // >= 44px BOTH axes. The label makes this button far wider today; the
        // floor is here so a shorter translation cannot drop it below.
        minWidth: 44,
        borderRadius: 10,
        gap: 7,
        fontSize: 13,
        fontWeight: 600,
        color: "var(--mod-accent)",
        borderColor: "var(--mod-accent-line)",
        background: "var(--mod-accent-soft)",
        position: "relative",
        flexShrink: 0,
      }}
    >
      <MbFilterGlyph />
      {translate("myMachinery:v2.fleet.filters")}
      {badge}
    </button>
  );
};
